import { invalidateWorkflowExecution, updateTask } from '@commenda/api';
import { Button, useToggle } from '@commenda/kakashi';
import { THumanFormStepDefinition } from '@commenda/types';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ConfirmationModal } from '../../../../molecules/modals/ConfirmationModal';
import { useMemo } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { ServiceRequestTaskLabel } from '../../labels';
import { HumanFormModal } from '../../modals/HumanFormModal';
import { useExportFormResponses } from '../../../hook/useExportFormResponses';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';
import { isCurrentTimelineTask } from '../../../utils';
import { WorkflowHelpResourceButtons } from './WorkflowHelpResourceButtons';

export const WorkflowHumanFormTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { setNotification } = useNotification();
  const { workflowExecution, mutate } = context;
  const humanFormModalVisibility = useToggle(false);
  const markAsCompleteModalVisibility = useToggle();
  const isMarkingAsComplete = useToggle(false);
  const { exportPdf, isExporting } = useExportFormResponses({
    serviceRequestId: context.serviceRequest?.id,
    taskId: task.id,
    entityName: context.serviceRequest?.corporation?.companyLegalName,
    title: task.title,
  });

  if (!workflowExecution?.WorkflowTemplateVersion?.definition)
    console.error('Workflow definition not found');
  if (!workflowExecution?.runtime?.stepExecutions)
    console.error('Workflow runtime step executions not found');

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    ) as THumanFormStepDefinition | undefined;
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const stepExecution = useMemo(() => {
    if (!workflowExecution?.runtime?.stepExecutions) return null;
    return Object.values(workflowExecution.runtime.stepExecutions).find(
      (execution) =>
        execution.stepDefinitionId === task.workflowDefinitionStepId
    );
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const formSubmissionId = useMemo(() => {
    if (context.workflowExecution?.runtime?.stepExecutions) {
      if (stepExecution?.outputData?.formSubmissionId) {
        return stepExecution.outputData.formSubmissionId as string;
      }
    }
    return null;
  }, [stepExecution]);

  const viewFormSubmissionButton = useMemo(() => {
    if (formSubmissionId) {
      return (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={humanFormModalVisibility.show}
          >
            View
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={exportPdf}
            isLoading={isExporting}
          >
            Export PDF
          </Button>
          <WorkflowHelpResourceButtons
            helpResources={stepDefinition.helpResources}
          />
        </div>
      );
    }
    return null;
  }, [formSubmissionId, isExporting]);

  const markFilingAsComplete = async () => {
    isMarkingAsComplete.on();
    const result = await updateTask({
      id: task.id,
      data: {
        status: TaskStatus.COMPLETED,
      },
    });

    if (!result.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to mark as complete',
      });
      markAsCompleteModalVisibility.hide();
      isMarkingAsComplete.off();
    }

    mutate();
    if (workflowExecution?.id) {
      invalidateWorkflowExecution(workflowExecution.id);
    }
    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Task marked as complete',
    });
    markAsCompleteModalVisibility.hide();
    isMarkingAsComplete.off();
  };

  if (!workflowExecution || !stepDefinition) {
    return (
      <div className="p-4 text-center">
        <p className="text-text-error txt-small-regular">
          Failed to load workflow step information
        </p>
      </div>
    );
  }

  if (!stepExecution) {
    return (
      <div className="p-4 text-center">
        <p className="text-text-error txt-small-regular">
          Step execution not found
        </p>
      </div>
    );
  }

  const { role } = context;
  const isCurrentTask = isCurrentTimelineTask(task);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions:
              task.pendingOn === TaskParty.CLIENT && isCurrentTask ? (
                <>
                  <Button size="sm" onClick={humanFormModalVisibility.show}>
                    Start
                  </Button>
                  <WorkflowHelpResourceButtons
                    helpResources={stepDefinition.helpResources}
                  />
                </>
              ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions:
              task.pendingOn === TaskParty.CLIENT && isCurrentTask ? (
                <>
                  <Button size="sm" onClick={humanFormModalVisibility.show}>
                    Proceed
                  </Button>
                  <WorkflowHelpResourceButtons
                    helpResources={stepDefinition.helpResources}
                  />
                </>
              ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: viewFormSubmissionButton,
            label: (
              <ServiceRequestTaskLabel
                label={`${task.title} completed`}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={
                    task.pendingOn === TaskParty.CLIENT
                      ? 'secondary'
                      : 'primary'
                  }
                  onClick={humanFormModalVisibility.show}
                >
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Start on client's behalf"
                    : 'Start'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive-secondary"
                  onClick={markAsCompleteModalVisibility.show}
                >
                  Mark as completed
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={
                    task.pendingOn === TaskParty.CLIENT
                      ? 'secondary'
                      : 'primary'
                  }
                  onClick={humanFormModalVisibility.show}
                >
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Continue on client's behalf"
                    : 'Continue'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive-secondary"
                  onClick={markAsCompleteModalVisibility.show}
                >
                  Mark as completed
                </Button>
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: viewFormSubmissionButton,
            label: (
              <ServiceRequestTaskLabel
                label={`${task.title} completed`}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.ADMIN]: {
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={
                    task.pendingOn === TaskParty.CLIENT
                      ? 'secondary'
                      : 'primary'
                  }
                  onClick={humanFormModalVisibility.show}
                >
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Start on client's behalf"
                    : 'Start'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive-secondary"
                  onClick={markAsCompleteModalVisibility.show}
                >
                  Mark as completed
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={humanFormModalVisibility.show}
                >
                  Continue
                </Button>
                <Button
                  size="sm"
                  variant="destructive-secondary"
                  onClick={markAsCompleteModalVisibility.show}
                >
                  Mark as completed
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: viewFormSubmissionButton,
            label: (
              <ServiceRequestTaskLabel
                label={`${task.title} completed`}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <HumanFormModal
        isOpen={humanFormModalVisibility.value}
        onClose={humanFormModalVisibility.off}
        formId={stepDefinition.formId}
        title={stepDefinition.name || task.title}
        description={stepDefinition.description}
        task={task}
        context={context}
        assigneeType={role}
      />
      <ConfirmationModal
        isOpen={markAsCompleteModalVisibility.visible}
        onClose={markAsCompleteModalVisibility.hide}
        onConfirm={markFilingAsComplete}
        title="Mark as done"
        message="Please note that this action will update the customer. To revert this change, please contact Commenda support."
        confirmButtonText="Continue"
        hideCancelButton
        isActionLoading={isMarkingAsComplete.value}
      />
    </div>
  );
};
